import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DURATION_UNITS, SUBSCRIPTION_TYPES } from './constants';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../../firebase/config';
import './dialogs.css';

const ExtendDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedUser,
  selectedSubscriptionType,
  formData,
  setFormData
}) => {
  const subscriptionInfo = SUBSCRIPTION_TYPES[selectedSubscriptionType.toUpperCase()];
  const currentSubscription = selectedUser?.subscriptions[selectedSubscriptionType];

  // # Helper function to calculate end date based on duration and unit
  const calculateEndDate = (startDate, duration, unit) => {
    const date = new Date(startDate);
    switch (unit) {
      case 'minutes':
        date.setMinutes(date.getMinutes() + duration);
        break;
      case 'days':
        date.setDate(date.getDate() + duration);
        break;
      case 'weeks':
        date.setDate(date.getDate() + (duration * 7));
        break;
      case 'months':
        date.setMonth(date.getMonth() + duration);
        break;
      default:
        return date;
    }
    return date;
  };

  // # Handle form submission with duration validation for Cloud Function
  const handleExtend = () => {
    if (!formData.duration || !formData.durationUnit) {
      return;
    }
    
    // # Calculate the new end date for notification
    const currentEndDate = new Date(currentSubscription.endDate);
    const newEndDate = calculateEndDate(
      currentEndDate,
      parseInt(formData.duration),
      formData.durationUnit
    );
    
    // # Create notification for the subscription extension
    const createNotification = httpsCallable(functions, 'createSubscriptionNotification');
    createNotification({
      uid: selectedUser.uid,
      subscriptionType: selectedSubscriptionType,
      startDate: currentSubscription.startDate,
      endDate: newEndDate.toISOString(),
      action: 'extend'
    })
    .then(notificationResult => {
      console.log('Subscription extension notification sent:', notificationResult.data);
    })
    .catch(error => {
      console.error('Error sending subscription extension notification:', error);
    });
    
    // # Pass the form data to parent component for Cloud Function processing
    onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="dialog-title">
        Extend {subscriptionInfo.name}
        {selectedUser && (
          <Typography variant="subtitle2" className="dialog-subtitle">
            for {selectedUser.userName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent className="dialog-content">
        <Typography className="info-text">
          This will extend the current subscription by the specified duration from its current end date.
        </Typography>
        <Typography variant="body2" color="textSecondary" className="form-field">
          Current subscription: {subscriptionInfo.name}
          <br />
          Current end date: {currentSubscription?.endDate ? new Date(currentSubscription.endDate).toLocaleDateString() : 'N/A'}
        </Typography>

        <Grid container spacing={2} className="form-field">
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              label="Duration"
              value={formData.duration || ''}
              onChange={(e) => {
                const value = Math.max(1, parseInt(e.target.value) || 1);
                setFormData({
                  ...formData,
                  duration: value.toString()
                });
              }}
              inputProps={{ min: 1 }}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth required>
              <InputLabel>Unit</InputLabel>
              <Select
                value={formData.durationUnit || 'months'}
                onChange={(e) => setFormData({
                  ...formData,
                  durationUnit: e.target.value
                })}
                label="Unit"
              >
                {DURATION_UNITS.map((unit) => (
                  <MenuItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Will extend by exactly: {formData.duration || '1'} {formData.durationUnit || 'months'}
        </Typography>

        {currentSubscription?.status !== 'active' && (
          <Typography color="error" sx={{ mt: 2 }}>
            Cannot extend an inactive subscription
          </Typography>
        )}
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={handleExtend}
          variant="contained" 
          color="primary"
          disabled={
            !formData.duration || 
            !formData.durationUnit || 
            currentSubscription?.status !== 'active'
          }
        >
          Extend Duration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtendDialog;
